import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import { MonitorSmartphone, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@staff-app/ui';
import type { AgentDevice } from '@staff-app/shared-types';
import { STAFF_REMOVE_AGENT_DEVICE } from '@staff-app/graphql-client';
import { useDeviceHeartbeats } from '@/features/mdm-devices/hooks/useDeviceHeartbeats';
import { useAgentDevices } from '../hooks/useAgentDevices';
import { AddAgentDeviceDialog } from '../components/AddAgentDeviceDialog';
import { AgentDeviceTable } from '../components/AgentDeviceTable';
import { SetAssignDialog } from '../components/SetAssignDialog';
import { BulkAssignDialog } from '../components/BulkAssignDialog';
import { summarizeAgentDeviceStatuses } from '../lib/device-status';
import { TestingDeviceDrawer } from '../components/testing-drawer/TestingDeviceDrawer';

interface RemoveResult {
  staffRemoveAgentDevice: AgentDevice[];
}

/**
 * My OptiSigns Testing Devices — the logged-in staff user's personal watchlist of
 * TESTING devices. Add a device by its Mongo _id; the api refuses any device outside
 * the OptiSigns Testing account (STAFF_TESTING_ACCOUNT_IDS). ROOT + SUPPORT only; it
 * only touches the caller's own admin_users.deviceLists. Rows added before that gate
 * may be customer devices: they stay listed but are flagged, and get no drawer actions.
 */
export function AgentDevicesPage() {
  const { devices, loading, error } = useAgentDevices();
  // Live online/HDMI/offline status from open-api's realtime heartbeat.
  const { statusByUuid } = useDeviceHeartbeats(devices.map((d) => d.device?.UUID));

  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AgentDevice | null>(null);
  const [editingAssign, setEditingAssign] = useState<AgentDevice | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  const [removeMutation, { loading: removeLoading }] = useMutation<RemoveResult>(
    STAFF_REMOVE_AGENT_DEVICE,
    { refetchQueries: ['staffAgentDevices'] },
  );

  const toggleCheck = (deviceId: string) =>
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });

  const toggleCheckAll = () =>
    setCheckedIds((prev) => {
      const ids = devices.map((d) => d.deviceId);
      const allChecked = ids.length > 0 && ids.every((id) => prev.has(id));
      return allChecked ? new Set() : new Set(ids);
    });

  const handleRemoveConfirmed = async () => {
    if (!removing) return;
    try {
      await removeMutation({ variables: { deviceId: removing.deviceId } });
      toast.success('Device removed from your list');
      if (selectedId === removing.deviceId) setSelectedId(null);
      setCheckedIds((prev) => {
        const next = new Set(prev);
        next.delete(removing.deviceId);
        return next;
      });
      setRemoving(null);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Unknown error';
      toast.error('Remove failed', { description: msg });
    }
  };

  // Bulk remove every checked device that's still in the list.
  const checkedDeviceIds = devices.map((d) => d.deviceId).filter((id) => checkedIds.has(id));
  // Only existing devices can be assigned content (skip removed/not-found rows).
  const checkedFoundDeviceIds = devices
    .filter((d) => d.found && checkedIds.has(d.deviceId))
    .map((d) => d.deviceId);
  const handleBulkRemoveConfirmed = async () => {
    const ids = checkedDeviceIds;
    if (!ids.length) return;
    // Run all removals independently so one failure doesn't skip the rest.
    const results = await Promise.allSettled(
      ids.map((deviceId) => removeMutation({ variables: { deviceId } })),
    );
    const succeededIds = ids.filter((_, i) => results[i].status === 'fulfilled');
    const failed = ids.length - succeededIds.length;

    if (failed === 0) {
      toast.success(`Removed ${succeededIds.length} device${succeededIds.length === 1 ? '' : 's'}`);
    } else {
      const firstErr = results.find((r) => r.status === 'rejected') as
        | PromiseRejectedResult
        | undefined;
      const msg = (firstErr?.reason as { message?: string })?.message ?? 'Unknown error';
      toast.error(`Removed ${succeededIds.length} of ${ids.length}`, {
        description: `${failed} could not be removed: ${msg}`,
      });
    }

    // Drop only the ones that succeeded; keep failures selected for retry.
    setCheckedIds((prev) => {
      const next = new Set(prev);
      succeededIds.forEach((id) => next.delete(id));
      return next;
    });
    if (selectedId && succeededIds.includes(selectedId)) setSelectedId(null);
    setBulkRemoveOpen(false);
  };

  const removingLabel = removing?.device?.deviceName || removing?.deviceId || '';
  const rollup = summarizeAgentDeviceStatuses(devices, statusByUuid);

  // Drawer: testing rows get the live MDM drawer; other rows get the pairing notice + row info.
  const selectedAgentDevice = devices.find((d) => d.deviceId === selectedId && d.device) ?? null;

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="agent-devices-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <MonitorSmartphone className="h-6 w-6" />
          {'My OptiSigns Testing Devices'}
        </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {devices.length} {devices.length === 1 ? 'device' : 'devices'} {'in your list'}
          </p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="min-w-32 cursor-pointer justify-center"
          data-testid="add-agent-device-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          {'Add device'}
        </Button>
      </div>

      <div
        className={`grid grid-cols-2 gap-3 ${rollup.hdmiOff > 0 ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}
        data-testid="agent-devices-summary"
      >
        <SummaryCard label={'Total devices'} value={rollup.total} />
        <SummaryCard label={'Online'} value={rollup.online} tone="text-success" />
        <SummaryCard label={'Stale'} value={rollup.stale} tone="text-warning" />
        {rollup.hdmiOff > 0 ? (
          <SummaryCard label={'HDMI off'} value={rollup.hdmiOff} tone="text-warning" />
        ) : null}
        <SummaryCard label={'Offline'} value={rollup.offline} tone="text-destructive" />
      </div>

      {error && devices.length ? (
        <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-warning">
          {'Some devices could not be loaded fully and may be incomplete.'}
        </div>
      ) : null}

      {error && !devices.length ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error.message}
        </div>
      ) : loading && !devices.length ? (
        <div className="text-sm text-muted-foreground">{'Loading your devices…'}</div>
      ) : devices.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-card p-10 text-center">
          <p className="text-sm font-medium text-foreground">{'No devices yet'}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {'Add a device by its Mongo _id to start tracking it here.'}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {checkedDeviceIds.length > 0 ? (
            <div
              className="fixed bottom-8 left-1/2 z-50 inline-flex -translate-x-1/2 items-center gap-1 rounded-xl bg-primary px-2 py-2 text-primary-foreground shadow-lg"
              data-testid="agent-devices-bulk-bar"
            >
              <button
                type="button"
                onClick={() => setCheckedIds(new Set())}
                className="mr-2 flex cursor-pointer items-center gap-2 rounded-md border border-primary-foreground/40 px-3 py-1.5 text-sm font-medium transition-colors hover:bg-primary-foreground/10"
                data-testid="agent-devices-bulk-clear"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
                {checkedDeviceIds.length} {'selected'}
              </button>

              {checkedFoundDeviceIds.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setBulkAssignOpen(true)}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors hover:bg-primary-foreground/10"
                  data-testid="agent-devices-bulk-assign"
                >
                  <Pencil className="h-4 w-4" />
                  {'Assign content'}
                </button>
              ) : null}

              <button
                type="button"
                onClick={() => setBulkRemoveOpen(true)}
                className="ml-1 flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors hover:bg-primary-foreground/10"
                data-testid="agent-devices-bulk-remove"
                aria-label="Remove selected"
                title="Remove selected"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ) : null}
          <div className="rounded-lg border bg-card">
            <AgentDeviceTable
              devices={devices}
              selectedId={selectedId}
              checkedIds={checkedIds}
              liveStatusByUuid={statusByUuid}
              onToggleCheck={toggleCheck}
              onToggleCheckAll={toggleCheckAll}
              onRowClick={(d) => d.found && setSelectedId(d.deviceId)}
              onRemove={(d) => setRemoving(d)}
              onEditAssign={(d) => setEditingAssign(d)}
            />
          </div>
        </div>
      )}

      <AddAgentDeviceDialog open={addOpen} onOpenChange={setAddOpen} />

      <SetAssignDialog
        device={editingAssign}
        onOpenChange={(o) => !o && setEditingAssign(null)}
      />

      <BulkAssignDialog
        open={bulkAssignOpen}
        deviceIds={checkedFoundDeviceIds}
        onOpenChange={setBulkAssignOpen}
        onSuccess={() => setCheckedIds(new Set())}
      />

      <TestingDeviceDrawer
        agentDevice={selectedAgentDevice}
        onClose={() => setSelectedId(null)}
        liveStatusByUuid={statusByUuid}
      />

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{'Remove device?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {'Remove '}
              <span className="font-medium text-foreground">{removingLabel}</span>
              {' from your list? You can add it back anytime.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleRemoveConfirmed();
              }}
              disabled={removeLoading}
              data-testid="agent-device-remove-confirm"
            >
              {removeLoading ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkRemoveOpen} onOpenChange={setBulkRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{'Remove selected devices?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {'Remove '}
              <span className="font-medium text-foreground">{checkedDeviceIds.length}</span>
              {' device'}
              {checkedDeviceIds.length === 1 ? '' : 's'}
              {' from your list? You can add them back anytime.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleBulkRemoveConfirmed();
              }}
              disabled={removeLoading}
              data-testid="agent-devices-bulk-remove-confirm"
            >
              {removeLoading ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm" data-testid={`agent-summary-${label}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${tone ?? 'text-foreground'}`}>{value}</div>
    </div>
  );
}
